import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, MenuItem, Modal, TextField, Typography } from '@mui/material';
import GenericDataGrid from '../../../components/genericTable';
import InlineAlert from '../../../components/inlineAlert';
import AlertToast from '../../../components/alertToast';
import { createManagementTableSx } from '../../../styles/tableStyles';
import { managementPageSubtitleSx } from '../../../styles/managementStyles';
import { resultMessage } from '../../../../service/apiResult';
import DeleteModal from '../subscriptionPlans/deleteModal';
import StatusModal from '../subscriptionPlans/statusModal';
import initiativePlansColumns from './initiativePlans.column';
import {
  deleteInitiativePlan, getInitiativePlanOptions, listInitiativePlans, setInitiativePlanStatus,
} from './initiativePlans.service';

const ALERT_DURATION = 2000;
const backdrop = { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } };

/** The "Initiative plans" tab of Subscriptions: what an area-based integrator sees when it joins an Initiative. */
export default function InitiativePlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [options, setOptions] = useState([]);
  const [initiativeId, setInitiativeId] = useState('');
  const [countryId, setCountryId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [alert, setAlert] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState('');

  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const result = await listInitiativePlans({ initiativeId, countryId });
    setLoading(false);
    if (result.success) {
      setPlans(result.data);
      setLoadError('');
    } else {
      setPlans([]);
      setLoadError(resultMessage(result, 'Could not load the Initiative plans.'));
    }
  }, [initiativeId, countryId]);

  useEffect(() => { setPage(0); load(); }, [load]);

  useEffect(() => {
    getInitiativePlanOptions().then((result) => result.success && setOptions(result.data));
  }, []);

  const countries = useMemo(() => {
    const byId = new Map();
    options.forEach((initiative) => (initiative.countries || []).forEach((c) => byId.set(c.id, c)));
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [options]);

  const columns = useMemo(() => initiativePlansColumns({
    onEdit: (plan) => navigate(`/addInitiativePlan/${plan.id}`),
    onStatus: (plan) => { setSelected(plan); setModal('status'); },
    onDelete: (plan) => { setSelected(plan); setModal('delete'); },
  }), [navigate]);

  const modalProps = {
    onClose: () => setModal(''),
    getSubscriptionData: load,
    selectedId: selected?.id,
    selectedStatus: selected?.status === 'ACTIVE',
    alertClose, setOpenAlert, setResponseMsg, setAlert, setLoading, ALERT_DURATION,
  };

  const gridSx = createManagementTableSx();
  const rows = plans.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <Box>
      <Typography sx={{ ...managementPageSubtitleSx, mb: 3, maxWidth: 760 }}>
        The plans an area-based integrator sees when it joins an Initiative. Set one plan per Initiative, country and
        pricing type; prices are in the country&apos;s currency.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <TextField select size="small" label="Initiative" value={initiativeId} sx={{ minWidth: { xs: '100%', sm: 240 } }}
          onChange={(e) => setInitiativeId(e.target.value)}>
          <MenuItem value="">All Initiatives</MenuItem>
          {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Country" value={countryId} sx={{ minWidth: { xs: '100%', sm: 240 } }}
          onChange={(e) => setCountryId(e.target.value)}>
          <MenuItem value="">All countries</MenuItem>
          {countries.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
      </Box>

      <InlineAlert
        message={loadError || (openAlert && alert === 'false' ? responseMsg : '')}
        onClose={() => { setLoadError(''); alertClose(); }}
        variant="error"
      />

      <Box sx={{ width: '100%' }}>
        <GenericDataGrid
          testId="initiative-plans-grid"
          rows={rows}
          columns={columns}
          rowCount={plans.length}
          page={page}
          pageSize={pageSize}
          loading={loading}
          errors={initiativeId || countryId ? 'No plans match these filters' : 'No Initiative plans yet'}
          checkbox="false"
          dataGridSx={gridSx}
          rowHeight={60}
          onPaginationChange={(nextPage, nextSize) => { setPage(nextPage); setPageSize(nextSize); }}
        />
      </Box>

      <Modal open={modal === 'status'} onClose={() => setModal('')} BackdropProps={backdrop}>
        <div><StatusModal {...modalProps} statusApi={setInitiativePlanStatus} /></div>
      </Modal>
      <Modal open={modal === 'delete'} onClose={() => setModal('')} BackdropProps={backdrop}>
        <div><DeleteModal {...modalProps} deleteApi={deleteInitiativePlan} /></div>
      </Modal>

      <Box textAlign="end">
        <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />
      </Box>
    </Box>
  );
}
